import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  defaultPipelineForMode,
  HERO_LIGHTING_PRESET,
  PRODUCT_LIGHTING_PRESET,
  type LightingConfig,
  type PublicRenderMode,
  type PublicRenderPipeline,
} from '../lib/publicRenderPipelines';
import {
  normalizeViewerEnvironmentSettings,
  type ViewerEnvironmentSettings,
} from '../lib/viewerEnvironmentRegistry';
import { defaultMaterialProperties, type MaterialProperties } from './useMaterialProperties';
import {
  type AdminPreviewState,
  type AdminPreviewCombinationDraft,
  type AdminPreviewCalibrationDraft,
} from './useAdminPreviewState';

const STORAGE_KEY = 'eco3dcor.visualDraft';
const LEGACY_PREVIEW_KEY = 'eco3dcor.adminPreview';
const MAX_COMBINATION_DRAFTS = 50;
const MAX_CALIBRATION_DRAFTS = 200;

type MaterialSettings = MaterialProperties;
type MaterialByFinish = Record<string, MaterialSettings>;

export type VisualCustomisationDraft = {
  pipelines: Record<PublicRenderMode, PublicRenderPipeline>;
  lighting: Record<PublicRenderMode, LightingConfig>;
  materials: MaterialByFinish;
  environmentRegistryDraft: ViewerEnvironmentSettings;
  combinationDraftsById: Record<string, AdminPreviewCombinationDraft>;
  calibrationDraftsByColourKey: Record<string, AdminPreviewCalibrationDraft>;
  adminPreview: AdminPreviewState;
};

export type VisualCustomisationDraftPatch = {
  pipelines?: Partial<Record<PublicRenderMode, Partial<PublicRenderPipeline>>>;
  lighting?: Partial<Record<PublicRenderMode, Partial<LightingConfig>>>;
  materials?: Partial<MaterialByFinish>;
  environmentRegistryDraft?: ViewerEnvironmentSettings;
  combinationDraftsById?: Record<string, AdminPreviewCombinationDraft | null>;
  calibrationDraftsByColourKey?: Record<string, AdminPreviewCalibrationDraft | null>;
  adminPreview?: Partial<AdminPreviewState>;
};

export type VisualCustomisationDraftStore = {
  draft: VisualCustomisationDraft;
  update: (patch: VisualCustomisationDraftPatch) => void;
  reset: () => void;
  discard: () => void;
  commit: (patch?: VisualCustomisationDraftPatch) => void;
  changeFlags: {
    pipeline: boolean;
    lighting: boolean;
    materials: boolean;
    environmentRegistry: boolean;
    combinations: boolean;
    calibration: boolean;
    preview: boolean;
  };
  hasUnsavedChanges: boolean;
};

const buildDefaults = (): VisualCustomisationDraft => ({
  pipelines: {
    hero: defaultPipelineForMode('hero'),
    product: defaultPipelineForMode('product'),
  },
  lighting: {
    hero: { ...HERO_LIGHTING_PRESET },
    product: { ...PRODUCT_LIGHTING_PRESET },
  },
  materials: { default: { ...defaultMaterialProperties } },
  environmentRegistryDraft: normalizeViewerEnvironmentSettings(null),
  combinationDraftsById: {},
  calibrationDraftsByColourKey: {},
  adminPreview: {
    previewMode: 'public-product',
    previewFinishType: 'shifting',
    previewVaseId: 1,
    previewCombinationId: '',
    showEnvironmentBackdrop: false,
    environmentBackdropBlur: 0,
    selectedColorIndex: 0,
    activeTab: 'combination',
  },
});

const normalizePreview = (preview: AdminPreviewState): AdminPreviewState => {
  const selectedIndex = Number.isFinite(preview.selectedColorIndex)
    ? Math.max(0, Math.floor(preview.selectedColorIndex ?? 0))
    : 0;
  const activeTab = preview.activeTab === 'calibration' ? 'calibration' : 'combination';
  const previewColourKey = preview.previewColourKey && preview.previewColourKey.trim()
    ? preview.previewColourKey
    : undefined;
  return {
    ...preview,
    previewMode: preview.previewMode === 'public-hero' ? 'public-hero' : 'public-product',
    previewFinishType: preview.previewFinishType && preview.previewFinishType.trim()
      ? preview.previewFinishType
      : 'shifting',
    showEnvironmentBackdrop: preview.showEnvironmentBackdrop === true,
    environmentBackdropBlur: Number.isFinite(preview.environmentBackdropBlur)
      ? Math.max(0, Math.min(1, Number(preview.environmentBackdropBlur)))
      : 0,
    selectedColorIndex: selectedIndex,
    activeTab,
    previewColourKey,
  };
};

const mergePipeline = (
  mode: PublicRenderMode,
  base: PublicRenderPipeline,
  incoming?: Partial<PublicRenderPipeline>
): PublicRenderPipeline => {
  if (!incoming) return base;
  return {
    ...base,
    ...incoming,
    id: incoming.id ?? base.id,
    mode,
    lighting: {
      ...base.lighting,
      ...(incoming.lighting ?? {}),
    },
  };
};

const mergeStoredDraft = (
  stored: Partial<VisualCustomisationDraft>,
  defaults: VisualCustomisationDraft
): VisualCustomisationDraft => {
  const normalizedPreview = normalizePreview({ ...defaults.adminPreview, ...(stored.adminPreview ?? {}) });
  const legacyCombinationDraft = normalizedPreview.previewCombinationDraft;
  const legacyCalibrationDraft = normalizedPreview.previewCalibrationDraft;
  const seededCombinationDrafts = { ...(stored.combinationDraftsById ?? {}) };
  const seededCalibrationDrafts = { ...(stored.calibrationDraftsByColourKey ?? {}) };

  if (legacyCombinationDraft?.id && !seededCombinationDrafts[legacyCombinationDraft.id]) {
    seededCombinationDrafts[legacyCombinationDraft.id] = legacyCombinationDraft;
  }
  if (legacyCalibrationDraft?.colourKey && !seededCalibrationDrafts[legacyCalibrationDraft.colourKey]) {
    seededCalibrationDrafts[legacyCalibrationDraft.colourKey] = legacyCalibrationDraft;
  }

  const nextPreview: AdminPreviewState = {
    ...normalizedPreview,
    previewCombinationDraft: undefined,
    previewCalibrationDraft: undefined,
  };

  return {
    pipelines: {
      hero: mergePipeline('hero', defaults.pipelines.hero, stored.pipelines?.hero),
      product: mergePipeline('product', defaults.pipelines.product, stored.pipelines?.product),
    },
    lighting: {
      hero: { ...defaults.lighting.hero, ...(stored.lighting?.hero ?? {}) },
      product: { ...defaults.lighting.product, ...(stored.lighting?.product ?? {}) },
    },
    materials: (() => {
      const legacyMaterial = (stored as { material?: MaterialSettings }).material;
      const legacyMaterials = (stored as { materials?: MaterialByFinish }).materials;
      const next: MaterialByFinish = { ...defaults.materials };
      if (legacyMaterials) {
        return { ...next, ...legacyMaterials };
      }
      if (legacyMaterial) {
        return { ...next, default: { ...next.default, ...legacyMaterial } };
      }
      return next;
    })(),
    environmentRegistryDraft: normalizeViewerEnvironmentSettings(stored.environmentRegistryDraft),
    combinationDraftsById: applyRecordPatch({}, seededCombinationDrafts, MAX_COMBINATION_DRAFTS),
    calibrationDraftsByColourKey: applyRecordPatch({}, seededCalibrationDrafts, MAX_CALIBRATION_DRAFTS),
    adminPreview: nextPreview,
  };
};

const readLegacyPreview = (fallback: AdminPreviewState): AdminPreviewState => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(LEGACY_PREVIEW_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<AdminPreviewState>;
    return normalizePreview({ ...fallback, ...parsed });
  } catch {
    return fallback;
  }
};

type StoredVisualCustomisationDraft = {
  draft?: Partial<VisualCustomisationDraft>;
  baseline?: Partial<VisualCustomisationDraft>;
};

const readStored = (
  defaults: VisualCustomisationDraft
): { value: VisualCustomisationDraft; baseline: VisualCustomisationDraft; hasDraft: boolean } => {
  if (typeof window === 'undefined') {
    return { value: defaults, baseline: defaults, hasDraft: false };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const value = {
        ...defaults,
        adminPreview: readLegacyPreview(defaults.adminPreview),
      };
      return {
        value,
        baseline: value,
        hasDraft: false,
      };
    }
    const parsed = JSON.parse(raw) as StoredVisualCustomisationDraft | Partial<VisualCustomisationDraft>;
    if (parsed && typeof parsed === 'object' && ('draft' in parsed || 'baseline' in parsed)) {
      const stored = parsed as StoredVisualCustomisationDraft;
      const draft = mergeStoredDraft(stored.draft ?? {}, defaults);
      const baseline = stored.baseline ? mergeStoredDraft(stored.baseline, defaults) : defaults;
      const hasDraft = stored.baseline ? !isSame(draft, baseline) : true;
      return { value: draft, baseline, hasDraft };
    }
    const draft = mergeStoredDraft(parsed as Partial<VisualCustomisationDraft>, defaults);
    return { value: draft, baseline: defaults, hasDraft: true };
  } catch {
    return { value: defaults, baseline: defaults, hasDraft: false };
  }
};

const isSame = (a: unknown, b: unknown) => {
  if (Object.is(a, b)) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

const applyRecordPatch = <T,>(
  current: Record<string, T>,
  patch?: Record<string, T | null>,
  cap?: number
): Record<string, T> => {
  if (!patch) return current;
  let next: Record<string, T> = { ...current };
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) {
      if (key in next) delete next[key];
      continue;
    }
    if (key in next) delete next[key];
    next[key] = value;
  }
  if (cap && Object.keys(next).length > cap) {
    const keys = Object.keys(next);
    const keep = keys.slice(keys.length - cap);
    const pruned: Record<string, T> = {};
    keep.forEach((key) => {
      pruned[key] = next[key];
    });
    next = pruned;
  }
  return next;
};

const mergePipelinePatch = (
  current: PublicRenderPipeline,
  patch?: Partial<PublicRenderPipeline>
) => {
  if (!patch) return current;
  const next = { ...current, ...patch };
  if (patch.lighting) {
    next.lighting = { ...current.lighting, ...patch.lighting };
  }
  return next;
};

const applyPatch = (
  current: VisualCustomisationDraft,
  patch: VisualCustomisationDraftPatch
): VisualCustomisationDraft => ({
  pipelines: patch.pipelines
    ? {
        hero: mergePipelinePatch(current.pipelines.hero, patch.pipelines.hero),
        product: mergePipelinePatch(current.pipelines.product, patch.pipelines.product),
      }
    : current.pipelines,
  lighting: patch.lighting
    ? {
        hero: patch.lighting.hero
          ? { ...current.lighting.hero, ...patch.lighting.hero }
          : current.lighting.hero,
        product: patch.lighting.product
          ? { ...current.lighting.product, ...patch.lighting.product }
          : current.lighting.product,
      }
    : current.lighting,
  materials: patch.materials ? { ...current.materials, ...patch.materials } : current.materials,
  environmentRegistryDraft: patch.environmentRegistryDraft
    ? normalizeViewerEnvironmentSettings(patch.environmentRegistryDraft)
    : current.environmentRegistryDraft,
  combinationDraftsById: patch.combinationDraftsById
    ? applyRecordPatch(current.combinationDraftsById, patch.combinationDraftsById, MAX_COMBINATION_DRAFTS)
    : current.combinationDraftsById,
  calibrationDraftsByColourKey: patch.calibrationDraftsByColourKey
    ? applyRecordPatch(
      current.calibrationDraftsByColourKey,
      patch.calibrationDraftsByColourKey,
      MAX_CALIBRATION_DRAFTS
    )
    : current.calibrationDraftsByColourKey,
  adminPreview: patch.adminPreview
    ? { ...current.adminPreview, ...patch.adminPreview }
    : current.adminPreview,
});

const VisualCustomisationDraftContext = createContext<VisualCustomisationDraftStore | null>(null);

const useVisualCustomisationDraftState = (): VisualCustomisationDraftStore => {
  const defaultsRef = useRef(buildDefaults());
  const initial = useMemo(() => readStored(defaultsRef.current), []);
  const [draft, setDraft] = useState<VisualCustomisationDraft>(initial.value);
  const [baseline, setBaseline] = useState<VisualCustomisationDraft>(initial.baseline);
  const [, setHasStoredDraft] = useState(initial.hasDraft);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ draft, baseline }));
  }, [draft, baseline]);

  const update = useCallback((patch: VisualCustomisationDraftPatch) => {
    setDraft(prev => {
      const next = applyPatch(prev, patch);
      return isSame(next, prev) ? prev : next;
    });
    setHasStoredDraft(true);
  }, []);

  const reset = useCallback(() => {
    const nextDefaults = buildDefaults();
    defaultsRef.current = nextDefaults;
    setDraft(nextDefaults);
    setBaseline(nextDefaults);
    setHasStoredDraft(false);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const discard = useCallback(() => {
    setDraft(baseline);
    setHasStoredDraft(false);
  }, [baseline]);

  const commit = useCallback((patch?: VisualCustomisationDraftPatch) => {
    setDraft((current) => {
      const nextDraft = patch ? applyPatch(current, patch) : current;
      setBaseline((currentBaseline) => (patch ? applyPatch(currentBaseline, patch) : nextDraft));
      setHasStoredDraft(false);
      return nextDraft;
    });
  }, []);

  const changeFlags = useMemo(() => {
    const pipeline =
      !isSame(draft.pipelines.hero, baseline.pipelines.hero) ||
      !isSame(draft.pipelines.product, baseline.pipelines.product);
    const lighting =
      !isSame(draft.lighting.hero, baseline.lighting.hero) ||
      !isSame(draft.lighting.product, baseline.lighting.product);
    const materials = !isSame(draft.materials, baseline.materials);
    const environmentRegistry = !isSame(draft.environmentRegistryDraft, baseline.environmentRegistryDraft);
    const combinations = !isSame(draft.combinationDraftsById, baseline.combinationDraftsById);
    const calibration = !isSame(draft.calibrationDraftsByColourKey, baseline.calibrationDraftsByColourKey);
    const preview = !isSame(draft.adminPreview, baseline.adminPreview);
    return { pipeline, lighting, materials, environmentRegistry, combinations, calibration, preview };
  }, [baseline, draft]);

  const hasUnsavedChanges = useMemo(
    () => Object.values(changeFlags).some(Boolean),
    [changeFlags]
  );

  return { draft, update, reset, discard, commit, changeFlags, hasUnsavedChanges };
};

export const VisualCustomisationDraftProvider = ({ children }: { children: ReactNode }) => {
  const store = useVisualCustomisationDraftState();
  return createElement(VisualCustomisationDraftContext.Provider, { value: store }, children);
};

export const useVisualCustomisationDraft = (): VisualCustomisationDraftStore => {
  const store = useContext(VisualCustomisationDraftContext);
  if (!store) {
    throw new Error('useVisualCustomisationDraft must be used within VisualCustomisationDraftProvider');
  }
  return store;
};

export const useOptionalVisualCustomisationDraft = (): VisualCustomisationDraftStore | null =>
  useContext(VisualCustomisationDraftContext);
